import { Component, Input, OnInit, AfterViewInit, ViewChild, ElementRef } from "@angular/core";
import { Router, NavigationStart } from '@angular/router';
import { CasingPlanModel } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { Subscription } from "rxjs";
import { filter, finalize } from "rxjs/operators";

import { GisLayerService } from "../../../gis/services/gis-layer.service";
import { GisViewService } from "../../../gis/services/gis-view.service";
import { GisLocationService } from "../../../gis/services/gis-location.service";
import { AuthService } from "../../../services/auth.service";
import { loadModules } from "esri-loader";

import { GisQueryService } from "../../../gis/services/gis-query.service";
import { GISConfig } from "../../../gis/models/gis-config.model";

import { WellService } from "../../../common/services/well.service"
import { Well, GISPointTypes, WellStub } from "../../../common/models/well.model";
import { Permit } from "../../../common/models/permit.model";
import { FlowIndex } from "../../../common/models/flow.dto";
import { PermitService } from "../../../common/services/permit.service";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { OrganizationService } from "../../../common/services/organization.service";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { DomainValue } from "../../../models/domain.model";


@Component({
    selector: "map-widget",
    templateUrl: "./map-widget.component.html",
    styleUrls: ["./map-widget.component.scss", "../../../gis/components/odnr-map/odnr-map.component.scss"],
    providers: [GisLayerService, GisViewService, GisLocationService, GisQueryService],
    standalone: false
})
export class MapWidget implements OnInit, AfterViewInit {
    casingPlans: Array<CasingPlanModel>;
    permits: Permit[] = [];
    approvedPermitsCount: Number;
    pendingPermitFlows: FlowIndex[];
    pendingPermitsCount: Number;
    wellCount: Number;
    displayMode: Number = 1;
    dataSubscriptions: Subscription = new Subscription();

    @ViewChild("mapViewNode", { static: true }) mapViewNode: ElementRef;

    @Input() latitude: number = -82.52419116144054;
    @Input() longitude: number = 39.87356744335349;
    @Input() widgetTitle: string;
    config: GISConfig;
    _allWellTypeDomainValues: DomainValue[];
    _allWellTypeDescriptionDomainValues: DomainValue[];
    _allWellDispositionDomainValues: DomainValue[];
    _allWellDispositionLegacyTrackingValues: DomainValue[];


    constructor(
        public readonly authService: AuthService,
        public readonly wellService: WellService,
        public readonly flowInstanceService: FlowInstanceService,
        public readonly organizationService: OrganizationService,
        public readonly domainService: DomainDefinitionService,
        public readonly router: Router
    ) { }

    ngOnInit(): void {
        this.config = window["GIS.Conf"] as GISConfig;

        //Fetch the well type domain values
        this.dataSubscriptions.add(this.domainService.getValues("WELL_TYP").pipe(
        ).subscribe(
            (domainValues: DomainValue[]) => {
                if (domainValues)
                    this._allWellTypeDomainValues = domainValues;
            },
            error => { console.log(error); console.log("ERROR Fetching the WELL_TYP domain."); }
        ));

        //Fetch all well type description domain values
        this.dataSubscriptions.add(this.domainService.getValues("WLTYP_DESC").pipe(
        ).subscribe(
            (domainValues: DomainValue[]) => {
                if (domainValues)
                    this._allWellTypeDescriptionDomainValues = domainValues;
            },
            error => { console.log(error); console.log("ERROR Fetching the WLTYP_DESC domain."); }
        ));

        //Fetch the well Disposition domain values
        this.dataSubscriptions.add(this.domainService.getValues("STATUS_DESC").pipe(
        ).subscribe(
            (domainValues: DomainValue[]) => {
                if (domainValues)
                    this._allWellDispositionDomainValues = domainValues;
            },
            error => { console.log(error); console.log("ERROR Fetching the STATUS_DESC domain."); }
        ));

        //Fetch all well Disposition LegacyTracking domain values
        this.dataSubscriptions.add(this.domainService.getValues("STATUS_REAS").pipe(
        ).subscribe(
            (domainValues: DomainValue[]) => {
                if (domainValues)
                    this._allWellDispositionLegacyTrackingValues = domainValues;
            },
            error => { console.log(error); console.log("ERROR Fetching the STATUS_REAS domain."); }
        ));

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.dataSubscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.dataSubscriptions.unsubscribe();
    }

    async initializeMap() {
        // Get user info for Org Id
        let orgId = this.authService.User.organizationId;
        if (this.mapViewNode) {
            try {

                const [Map, MapView, WatchUtils] = await loadModules([
                    "esri/Map",
                    "esri/views/MapView",
                    "esri/core/watchUtils"
                ]);
                const map = new Map({
                    basemap: "streets"
                });

                const view = new MapView({
                    container: "mapViewNode",
                    map: map,
                    zoom: 7,
                    center: [this.latitude, this.longitude]
                });

                this.dataSubscriptions.add(this.organizationService.getOrganizationStub(orgId).subscribe(orgResult => {
                    let orgIds = [ orgId, orgResult.historicalOwnerNumber ];
                    view.when(() => {
                        this.dataSubscriptions.add(this.wellService.getCurrentAndHistoricalWellStubs(orgIds).subscribe((result) => {
                            if (result)
                                this.wellCount = result.length;
                            this.placeWellsOnMap(result, map, view);
                        }, (error) => {
                            console.log(error);
                        }));
                        this.dataSubscriptions.add(this.flowInstanceService.queryFlowIndices("PendingPermitApplications", orgId).subscribe((result: FlowIndex[]) => {
                            if (result)
                                this.pendingPermitsCount = result.length;
                            this.placePendingPermitsOnMap(result, map, view);
                        }, (error) => {
                            console.log(error);
                        }));
                    });
                }, (error) => {
                    console.log(error);
                }));

            } catch (error) {
                console.log(`We have an error: ${error}`);
            }
        }
    }

    public async placePendingPermitsOnMap(pendingPermitFlows: any[], map: __esri.Map, view: __esri.MapView) {
        try {
            const [Point, Graphic, GraphicsLayer] = await loadModules([
                "esri/geometry/Point",
                "esri/Graphic",
                "esri/layers/GraphicsLayer"
            ]);

            let tempArray = [];

            if (pendingPermitFlows) {
                // Create points
                pendingPermitFlows.forEach(f => {


                    // Create point
                    const pendingPermitPoint = new Point({
                        longitude: f.longitude,
                        latitude: f.latitude
                    });

                    // Add popup info
                    const pointAttributes = {
                        "pointType": "permit",
                        "permitNumber": f.flowIdentifier ? f.flowIdentifier : "",
                        "permitType": f.name ? f.name : "",
                        "wellType": f.wellType ? f.wellType : "",
                        "slant": f.slant ? f.slant : "",
                        "latitude": pendingPermitPoint.latitude ? pendingPermitPoint.latitude : "",
                        "longitude": pendingPermitPoint.longitude ? pendingPermitPoint.longitude : "",
                        "dateCreated": f.createdDate ? f.createdDate : "",
                    };
                    // Create symbol
                    const markerSymbol = {
                        type:
                            "simple-marker", // autocasts as new SimpleMarkerSymbol()
                        color: [166, 195, 107],
                        outline: { // autocasts as new SimpleLineSymbol()
                            color: [0, 0, 0],
                            width: 0.5
                        }
                    };
                    // Create graphic
                    const pointGraphic = new Graphic({
                        geometry: pendingPermitPoint,
                        symbol: markerSymbol,
                        attributes: pointAttributes
                    });
                    tempArray.push(pointGraphic);

                });
            }
            // Create layer and add points
            const pointLayer = new GraphicsLayer({
                id: "proposedPointLayer",
                name: "Well Head",
                graphics: tempArray
            });

            if (map && map.initialized) {
                map.layers.add(pointLayer);
            }

            view.on("click",
                (event) => {
                    this.mapClickEvent(event, view);
                });

        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    public async placeWellsOnMap(wells: WellStub[], map: __esri.Map, view: __esri.MapView) {
        try {
            const [Point, Graphic, GraphicsLayer] = await loadModules([
                "esri/geometry/Point",
                "esri/Graphic",
                "esri/layers/GraphicsLayer"
            ]);

            let tempArray = [];

            if (wells) {
                this.wellCount = wells.length;
                // Create points
                wells.forEach(w => {

                    // Create point
                    let point = w.location.gisPoints.find(x => x.pointType == GISPointTypes.WellHead);

                    if (point && point.coordinates) {
                        const wellPoint = new Point({
                            longitude: point.coordinates.longitude,
                            latitude: point.coordinates.latitude
                        });

                        // Add popup info
                        const pointAttributes = {
                            "pointType": "well",
                            "wellType": w.wellStatus.type ? w.wellStatus.type.type : "",
                            "wellTypeDescription": w.wellStatus.type ? w.wellStatus.type.description : "",
                            "wellDisposition": w.wellStatus.disposition,
                            "wellDispositionLegacyTracking": w.wellStatus.legacyTracking,
                            "permitNumber": w.permitNumber ? w.permitNumber : "",
                            "organizationsWellNumber": w.wellNumber ? w.wellNumber : "",
                            "latitude": point.coordinates.latitude ? point.coordinates.latitude : "",
                            "longitude": point.coordinates.longitude ? point.coordinates.longitude : "",
                            "dateCompleted": w.dateCompleted ? w.dateCompleted : "",
                            "leaseName": w.lease ? w.lease.name : "",
                            "slant": w.location.slant ? w.location.slant : "",
                            "isCompliant": w.compliance ? w.compliance.isCompliant : false,
                            "apiNumber": w.id ? w.id : false
                        };
                        // Create symbol
                        const markerSymbol = {
                            type:
                                "simple-marker", // autocasts as new SimpleMarkerSymbol()
                            color: [226, 119, 40],
                            outline: { // autocasts as new SimpleLineSymbol()
                                color: [0, 0, 0],
                                width: 0.5
                            }
                        };
                        // Create graphic
                        const pointGraphic = new Graphic({
                            geometry: wellPoint,
                            symbol: markerSymbol,
                            attributes: pointAttributes
                        });
                        tempArray.push(pointGraphic);
                    }
                });
            }
            // Create layer and add points
            const pointLayer = new GraphicsLayer({
                id: "proposedPointLayer",
                name: "Well Head",
                graphics: tempArray
            });

            if (map && map.initialized) {
                map.layers.add(pointLayer);
            }

            view.on("click",
                (event) => {
                    this.mapClickEvent(event, view);
                });

        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }



    public mapClickEvent(event: __esri.MapViewClickEvent, view: __esri.MapView) {
        view.hitTest(event).then(response => {
            if (response.results[0].graphic) {
                let pointType = response.results[0].graphic.getAttribute("pointType");
                //console.log(response.results[0]);
                view.popup.dockEnabled = false;
                event.stopPropagation();

                if (pointType == "permit") {
                    view.popup.open({
                        location: event.mapPoint,
                        title: "",
                        content:
                            `Permit Number: ${response.results[0].graphic.getAttribute("permitNumber")}
                                        <br />Type: ${response.results[0].graphic.getAttribute("permitType")}
                                        <br />Well Type: ${this.getWellPropertyFromDomain('wellType', response.results[0].graphic.getAttribute("wellType"))}
                                        <br />Type Description: ${this.getWellPropertyFromDomain('wellTypeDescription', response.results[0].graphic.getAttribute("wellTypeDescription"))}
                                        <br />Slant: ${response.results[0].graphic.getAttribute("slant")}
                                        <br />Latitude: ${response.results[0].graphic.getAttribute("latitude")}
                                        <br />Longitude: ${response.results[0].graphic.getAttribute("longitude")}
                                        <br />Date Created: ${response.results[0].graphic.getAttribute("dateCreated")}`
                    });
                }

                if (pointType == "well") {
                    view.popup.open({
                        location: event.mapPoint,
                        title: "",
                        content:
                            `Permit Number: ${response.results[0].graphic.getAttribute("permitNumber")}
                                        <br />API Number: ${response.results[0].graphic.getAttribute("apiNumber")}
                                        <br />Organization's Well Number: ${response.results[0].graphic.getAttribute("organizationsWellNumber")}
                                        <br />Type: ${this.getWellPropertyFromDomain('wellType', response.results[0].graphic.getAttribute("wellType"))}
                                        <br />Type Description: ${this.getWellPropertyFromDomain('wellTypeDescription', response.results[0].graphic.getAttribute("wellTypeDescription"))}
                                        <br />Disposition: ${this.getWellPropertyFromDomain('wellDisposition', response.results[0].graphic.getAttribute("wellDisposition"))}
                                        <br />Disposition LegacyTracking: ${this.getWellPropertyFromDomain('wellDispositionLegacyTracking', response.results[0].graphic.getAttribute("wellDispositionLegacyTracking"))}
                                        <br />Slant: ${response.results[0].graphic.getAttribute("slant")}   
                                        <br />Latitude: ${response.results[0].graphic.getAttribute("latitude")}
                                        <br />Longitude: ${response.results[0].graphic.getAttribute("longitude")}
                                        <br />Date Completed: ${response.results[0].graphic.getAttribute("dateCompleted")}
                                        <br />Lease Name: ${response.results[0].graphic.getAttribute("leaseName")}
                                        <br />Is Compliant: ${response.results[0].graphic.getAttribute("isCompliant")}<br />`
                    });
                }
            }
        });
    }


    public getWellPropertyFromDomain(domainType:string, code: string): string {
        let domainDefinition = "";
        let domainValues: DomainValue[];
        if (domainType && code) {
            switch (domainType) {
                case 'wellType':
                    domainValues = this._allWellTypeDomainValues;
                    break;
                case 'wellTypeDescription':
                    domainValues = this._allWellTypeDescriptionDomainValues;
                    break;
                case 'wellDisposition':
                    domainValues = this._allWellDispositionDomainValues;
                    break;
                case 'wellDispositionLegacyTracking':
                    domainValues = this._allWellDispositionLegacyTrackingValues;
                    break;
            }

            let matchingDomainValues = domainValues.filter(a => a.Code == code);
            if (matchingDomainValues && matchingDomainValues.length > 0)
                domainDefinition = matchingDomainValues[0].Definition;
        }

        return domainDefinition;
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initializeMap();
        },
            5000);
    }

    routeToWellViewer(): void {
        this.router.navigate(["/gis/well-viewer/"]);
    }
}
